import { DateTime } from 'luxon';
// `Model` from its own module, not the `@/models` barrel — see the note in `index.ts`.
import { Model } from './model';
import { EntryModel, type NetworkEntry, type FormEnforcements, type NetworkForm } from '@/models';

export interface NetworkCases {
    cases: ICase[],
    casesCount: number
}

interface LockUnlockObject {
  identifier: string;
  revisionIdentifier: string;
}

export interface CaseFormData {
  forms: NetworkForm[]
}

export interface PostLockUnlockResource {
  cases?: LockUnlockObject[];
  responses?: LockUnlockObject[];
  status: string;
}

export class CaseModel extends Model {
  identifier: string;
  createdAt: Date;
  label: string;
  entries?: Record<string, EntryModel[]>;
  enforcements?: Record<string, FormEnforcements>;
  hidden: boolean;
  updatedAt: Date;
  deletedAt: Date | null;
  lockedAt: Date | null;
  creatorIdentifier: string;
  creatorNames: string;
  creatorAvatar: string;

  archivedAt?: Date;

  loadingData?: boolean;
  isFinished?: boolean;
  shiftKey?: number;
  caseNoteCount?: number;

  constructor(iCase:ICase) {
    super();
    this.identifier = iCase.identifier;
    this.createdAt = DateTime.fromISO(iCase.createdAt).toJSDate();
    this.label = iCase.label;
    this.entries = iCase.caseEntries;
    this.hidden = iCase.hidden;
    this.updatedAt = DateTime.fromISO(iCase.updatedAt).toJSDate();
    this.deletedAt = iCase.deletedAt ? DateTime.fromISO(iCase.deletedAt).toJSDate() : null;
    this.lockedAt = iCase.lockedAt ? DateTime.fromISO(iCase.lockedAt).toJSDate() : null;
    this.archivedAt = iCase.archivedAt ? DateTime.fromISO(iCase.archivedAt).toJSDate() : undefined;
    this.creatorIdentifier = iCase.creatorIdentifier;
    this.creatorNames = iCase.creatorNames;
    this.creatorAvatar = iCase.creatorAvatar;
    this.loadingData = iCase.loadingData;
    this.isFinished = iCase.isFinished;
    this.enforcements = iCase.enforcements;
    this.shiftKey = iCase.shiftKey;
    this.caseNoteCount = iCase.caseNoteCount;
  }

  static mapFromBackend(caseItem: ICase) {
    const organizedEntries: Record<string, EntryModel[]> = {};

    if (caseItem.entries) {
      for (const entry of caseItem.entries as NetworkEntry[]) {
        const formId = entry.formIdentifier;

        if (!organizedEntries[formId]) {
          organizedEntries[formId] = [];
        }

        organizedEntries[formId].push(EntryModel.mapFromBackend(entry));
      }
    }

    return new CaseModel({
      identifier: caseItem.identifier,
      createdAt: caseItem.createdAt,
      label: caseItem.label,
      hidden: caseItem.hidden,
      caseEntries: caseItem.entries ? organizedEntries : undefined,
      updatedAt: caseItem.updatedAt,
      deletedAt: caseItem.deletedAt,
      lockedAt: caseItem.lockedAt,
      creatorIdentifier: caseItem.creatorIdentifier,
      creatorNames: caseItem.creatorNames,
      creatorAvatar: caseItem.creatorAvatar,
      archivedAt: caseItem.archivedAt,
      loadingData: false,
      enforcements: caseItem.enforcements,
      isFinished: caseItem.isFinished,
      shiftKey: caseItem.shiftKey,
      caseNoteCount: caseItem.caseNoteCount
    });
  }

  static createEmpty(identifier: string) {
    return new CaseModel({
      identifier,
      createdAt: '',
      label: '',
      hidden: false,
      caseEntries: undefined,
      updatedAt: '',
      deletedAt: '',
      lockedAt: '',
      creatorIdentifier: '',
      creatorNames: '',
      creatorAvatar: '',
      archivedAt: '',
      loadingData: false,
      enforcements: undefined,
      isFinished: false,
      shiftKey: undefined
    });
  }
}


export interface ICase {
  identifier: string;
  createdAt: string;
  label: string;
  hidden: boolean;
  entries?: NetworkEntry[],
  caseEntries?: Record<string, EntryModel[]>,
  enforcements?: Record<string, FormEnforcements>;
  updatedAt: string;
  deletedAt: string | null;
  lockedAt: string | null;
  creatorIdentifier: string;
  creatorNames: string;
  creatorAvatar: string;
  archivedAt?: string;
  loadingData?: boolean;
  isFinished?: boolean;
  shiftKey?: number;
  caseNoteCount?: number;
}

// eslint-disable-next-line no-shadow
export enum PumpPipelineFilter {
  ACTIVE='Active',
  FINISHED='Finished',
  ALL='All'
}
